import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { History, CheckCircle2, XCircle, Loader2, PauseCircle, ChevronRight } from "lucide-react";
import type { AgentExecution } from "@/types/api";
import { formatDateTime } from "@/lib/datetime";

interface ExecutionHistoryProps {
  executions: AgentExecution[];
  selectedExecutionId?: string | null;
  latestExecutionId?: string | null;
  onSelect: (executionId: string | null) => void;
  /** Tone of the trigger button. "dark" for hero backgrounds, "light" (default)
   * for normal white backgrounds. The drawer content is unaffected. */
  triggerTone?: "light" | "dark";
}

function formatDuration(start?: string | null, end?: string | null): string {
  if (!start || !end) return "-";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return "<1s";
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remSecs = secs % 60;
  return `${mins}m ${remSecs}s`;
}

const STATUS_CONFIG = {
  completed: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-500/15 border-emerald-500/30", label: "Completed" },
  failed: { icon: XCircle, color: "text-red-500", bg: "bg-red-500/15 border-red-500/30", label: "Failed" },
  running: { icon: Loader2, color: "text-blue-500", bg: "bg-blue-500/15 border-blue-500/30", label: "Running" },
  paused: { icon: PauseCircle, color: "text-amber-500", bg: "bg-amber-500/15 border-amber-500/30", label: "Paused" },
  pending: { icon: Loader2, color: "text-muted-foreground", bg: "bg-muted border-border", label: "Pending" },
};

export function ExecutionHistory({
  executions,
  selectedExecutionId,
  latestExecutionId,
  onSelect,
  triggerTone = "light",
}: ExecutionHistoryProps) {
  const [open, setOpen] = useState(false);
  const completedCount = executions.filter(e => e.status === "completed").length;

  const handleSelect = (id: string | null) => {
    onSelect(id);
    setOpen(false);
  };

  // The hero is dark (#08254c), so the default outline button — black text
  // on white — disappears against it until hover. The "dark" tone mirrors
  // the styling of Configure/New Run on the hero so the action set reads
  // as a coherent group.
  const triggerClass =
    triggerTone === "dark"
      ? "h-[30px] bg-white/[0.08] hover:bg-white/[0.14] text-white border border-white/[0.18] gap-1.5 px-3 text-[11px] font-semibold"
      : "h-8 text-xs gap-1.5";
  const triggerVariant = triggerTone === "dark" ? "ghost" : "outline";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant={triggerVariant} size="sm" className={triggerClass}>
          <History className="h-3.5 w-3.5" />
          Past Runs ({executions.length})
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[440px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Execution History
          </SheetTitle>
          <p className="text-xs text-muted-foreground">
            {completedCount} completed out of {executions.length} total runs
          </p>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-8rem)] mt-4 -mx-6 px-6">
          <div className="space-y-3">
            {executions.map((exec, idx) => {
              const runNumber = executions.length - idx;
              const cfg = STATUS_CONFIG[exec.status] || STATUS_CONFIG.pending;
              const StatusIcon = cfg.icon;
              const isLatest = exec.id === latestExecutionId;
              const isSelected = selectedExecutionId === null ? isLatest : selectedExecutionId === exec.id;
              const summary = exec.output_summary as Record<string, unknown> | null;
              const metrics = exec.metrics;

              const scopeCount = (summary?.scope_items_count as number) ?? (summary?.scope_items as unknown[])?.length ?? 0;
              const gapsCount = (summary?.gaps_count as number) ?? 0;
              const cost = metrics?.cost_estimate ?? 0;

              return (
                <div
                  key={exec.id}
                  className={`rounded-lg border p-3 transition-colors cursor-pointer hover:bg-muted/50 ${
                    isSelected ? "border-primary bg-primary/5" : "border-border"
                  }`}
                  onClick={() => handleSelect(isLatest ? null : exec.id)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">Run #{runNumber}</span>
                      <Badge variant="outline" className={`text-[10px] ${cfg.bg} ${cfg.color}`}>
                        <StatusIcon className={`h-3 w-3 mr-1 ${exec.status === "running" ? "animate-spin" : ""}`} />
                        {cfg.label}
                      </Badge>
                      {isLatest && (
                        <Badge variant="secondary" className="text-[10px]">Latest</Badge>
                      )}
                    </div>
                    {isSelected && (
                      <span className="text-[10px] font-medium text-primary">Active</span>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground">
                    {formatDateTime(exec.started_at)}
                  </p>

                  {exec.status === "completed" && (
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                      {scopeCount > 0 && <span>{scopeCount} scope items</span>}
                      {gapsCount > 0 && <span className="text-amber-500">{gapsCount} gaps</span>}
                      {cost > 0 && <span>${cost.toFixed(2)}</span>}
                      <span>{formatDuration(exec.started_at, exec.completed_at)}</span>
                    </div>
                  )}

                  {exec.status === "failed" && exec.error_details && (
                    <p className="text-[11px] text-red-400 mt-1.5 line-clamp-2">
                      {exec.error_details}
                    </p>
                  )}

                  {!isSelected && exec.status === "completed" && (
                    <div className="flex justify-end mt-2">
                      <span className="text-[11px] text-primary flex items-center gap-0.5">
                        Load <ChevronRight className="h-3 w-3" />
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
